import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@auth/AuthProvider';
import PlaylistsController from '@data/controller/PlaylistsController';
import Playlist, { PlaylistAttributes } from '@data/interfaces/playlist.interface';

export default function usePlaylists(providedChurchId?: string) {
  const auth = useAuth();
  const churchId = providedChurchId || auth.churchId;

  const [playlists, setPlaylists] = useState<Playlist[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!churchId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await PlaylistsController.fetchPlaylists(churchId, auth);
      if (response.error) {
        setError(response.error);
        setPlaylists(null);
      } else if (response.body) {
        setPlaylists(response.body);
      } else {
        setPlaylists(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load playlists');
      setPlaylists(null);
    } finally {
      setLoading(false);
    }
  }, [churchId, auth]);

  useEffect(() => {
    if (!churchId) return;

    void fetch();
  }, [churchId, fetch]);

  const createPlaylist = useCallback(async (attributes: PlaylistAttributes) => {
    if (!churchId) {
      return {
        error: 'Missing church ID.'
      };
    }

    const response = await PlaylistsController.createPlaylist(churchId, attributes, auth);
    if (response.error) {
      return {
        error: response.error
      };
    }

    if (response.body) {
      setPlaylists((currentPlaylists) => {
        if (currentPlaylists === null) {
          return [response.body!];
        }

        return [response.body!, ...currentPlaylists];
      });
    }

    return {
      playlist: response.body ?? null
    };
  }, [churchId, auth]);

  return { playlists, loading, error, refetch: fetch, createPlaylist } as const;
}
